import {
  createContext,
  Fragment,
  useCallback,
  useContext,
  useId,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { analyze } from "../../shared/dwsql/index.js";

/**
 * Client-side data layer for dwsql parameters. See data/DataLayer.md.
 *
 * Embeds register the params they use (`useBoundQuery`); the provider collects
 * them into a flat, page-scoped set and exposes their values. Values live in
 * provider state (not the URL): URL params interact badly with editing (the
 * `?edit` flag, the unsaved-changes blocker, raw-editor enter/exit). A future
 * pass can reintroduce bookmarkable filters once that's reconciled.
 *
 * v0 is deliberately minimal: a single top filter bar (`<PageFilters>`), plain
 * `<input>`s, no range support, and none of the `:::param` / `:::filters`
 * directives (those slot in later without changing this layer).
 */

export interface ParamSpec {
  name: string;
  /** Declared `::type`, lower-cased downstream for the input widget. */
  type: string | null;
  /** True when the SQL carries a `-- $name = default`, so it isn't required. */
  hasDefault: boolean;
}

interface DwSqlContextValue {
  /** Page path — the @ref search base and (server-side) include root. */
  path: string | null;
  /** Distinct params across every embed on the page, in registration order. */
  specs: ParamSpec[];
  /** Current values, keyed by name (missing => unfilled). */
  values: Record<string, string>;
  register: (id: string, specs: ParamSpec[]) => void;
  unregister: (id: string) => void;
  setValue: (name: string, value: string) => void;
}

const DwSqlContext = createContext<DwSqlContextValue | null>(null);

function useDwSql(): DwSqlContextValue {
  const ctx = useContext(DwSqlContext);
  if (!ctx) throw new Error("useDwSql must be used within <DwSqlProvider>");
  return ctx;
}

export function DwSqlProvider({
  path,
  children,
}: {
  path: string | null;
  children: ReactNode;
}) {
  // One entry per embed, keyed by its stable id; aggregated into `specs`.
  const [byEmbed, setByEmbed] = useState<Record<string, ParamSpec[]>>({});
  const [values, setValues] = useState<Record<string, string>>({});

  const register = useCallback((id: string, specs: ParamSpec[]) => {
    setByEmbed((prev) => ({ ...prev, [id]: specs }));
  }, []);

  const unregister = useCallback((id: string) => {
    setByEmbed((prev) => {
      if (!(id in prev)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const setValue = useCallback((name: string, value: string) => {
    setValues((prev) => {
      if (value === "") {
        if (!(name in prev)) return prev;
        const next = { ...prev };
        delete next[name];
        return next;
      }
      if (prev[name] === value) return prev;
      return { ...prev, [name]: value };
    });
  }, []);

  // Flatten embeds' params into one page-scoped set; first declaration of a
  // name wins (the flat namespace means a name has one type page-wide anyway).
  const specs = useMemo(() => {
    const seen = new Map<string, ParamSpec>();
    for (const list of Object.values(byEmbed)) {
      for (const s of list) if (!seen.has(s.name)) seen.set(s.name, s);
    }
    return [...seen.values()];
  }, [byEmbed]);

  const ctx = useMemo<DwSqlContextValue>(
    () => ({ path, specs, values, register, unregister, setValue }),
    [path, specs, values, register, unregister, setValue],
  );

  return <DwSqlContext.Provider value={ctx}>{children}</DwSqlContext.Provider>;
}

export interface BoundQuery {
  /** Raw dwsql source to send to the server (it compiles + binds). */
  sql: string;
  /** Values to bind, keyed by name — only the filled ones for this query. */
  values: Record<string, string>;
  /** Page path, for @ref resolution. */
  path: string | null;
  /** "blocked" when a required (no-default) param is still unfilled. */
  status: "ready" | "blocked";
  /** Required params with no value yet (drives the fill prompt). */
  missing: string[];
}

/**
 * Viz-side hook. Analyzes the embed's dwsql, registers its params on the page,
 * and reports whether it's runnable. Call `useQuery` only when `status` is
 * "ready" (pass a null sql otherwise) so a blocked query never fires.
 */
export function useBoundQuery(source: string): BoundQuery {
  const ctx = useDwSql();
  const id = useId();
  const { register, unregister } = ctx;

  const specs = useMemo<ParamSpec[]>(
    () =>
      analyze(source).params.map((p) => ({
        name: p.name,
        type: p.type,
        hasDefault: p.hasDefault,
      })),
    [source],
  );

  // Register before paint so the filter bar is correct on first render.
  useLayoutEffect(() => {
    register(id, specs);
    return () => unregister(id);
  }, [id, specs, register, unregister]);

  const values: Record<string, string> = {};
  const missing: string[] = [];
  for (const s of specs) {
    const v = ctx.values[s.name];
    if (v !== undefined && v !== "") values[s.name] = v;
    else if (!s.hasDefault) missing.push(s.name);
  }

  return {
    sql: source,
    values,
    path: ctx.path,
    status: missing.length > 0 ? "blocked" : "ready",
    missing,
  };
}

/** Shown by a viz in place of results while required params are unfilled. */
export function FillPrompt({ names }: { names: string[] }) {
  return (
    <div className="dw-fill-prompt">
      Fill in{" "}
      {names.map((n, i) => (
        <Fragment key={n}>
          {i > 0 && ", "}
          <code>{n}</code>
        </Fragment>
      ))}{" "}
      to run.
    </div>
  );
}

/** A single text/number/date input — no range, no fancy widgets (v0). */
function inputType(sqlType: string | null): string {
  const t = (sqlType ?? "").toLowerCase();
  if (/^(small|big)?int|^int[248]?\b|^numeric|^decimal|^real|^double|^float/.test(t))
    return "number";
  if (t.startsWith("date")) return "date";
  return "text";
}

/**
 * Page-level filter bar: one input per registered param. Renders nothing when
 * the page has no params. Lives at the top of the page (inside the provider).
 */
export function PageFilters() {
  const { specs, values, setValue } = useDwSql();
  if (specs.length === 0) return null;
  return (
    <section className="dw-filters">
      {specs.map((s) => (
        <label key={s.name} className="dw-filter">
          <span className="dw-filter-name">{s.name}</span>
          <input
            className="dw-filter-input"
            type={inputType(s.type)}
            value={values[s.name] ?? ""}
            placeholder={s.type ?? "text"}
            onChange={(e) => setValue(s.name, e.target.value)}
          />
        </label>
      ))}
    </section>
  );
}
